"use client";

import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CaseStudies() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const tales = [{
    id: 1,
    badge: "OTA Strategy",
    title: "Boosting OTA Visibility",
    teaser: "How we optimized property listings to increase direct bookings and revenue for a boutique hotel in a competitive market.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_optimization.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_optimization.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_optimization.webp-wvc-sizes",
    alt: "OTA optimization case study showing increased visibility and direct booking conversion",
    link: "/OTARevenueManagementListingsPage",
    linkText: "See how OTA optimization improved RevPAR"
  }, {
    id: 2,
    badge: "Tech Stack",
    title: "Modernizing Hotel Tech",
    teaser: "Implementing a seamless PMS and channel manager integration to streamline operations and reduce manual errors.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_implementation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_implementation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_implementation.png-wvc-sizes",
    alt: "Hotel tech stack implementation dashboard",
    link: "/HotelTechStackConsultingPage",
    linkText: "Explore our tech stack modernization results"
  }, {
    id: 3,
    badge: "Audit",
    title: "Revenue Audit Success",
    teaser: "A comprehensive revenue audit that uncovered hidden pricing opportunities and improved RevPAR by 25% in one quarter.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-sizes",
    alt: "Revenue audit success showing 25% RevPAR improvement",
    link: "/HotelRevenueAuditFreePaidPage",
    linkText: "View the full revenue audit breakdown"
  }];
  return <section data-section-id="11789" id="hotelcasestudies" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1240px] px-6 md:px-10 lg:px-16">
        <div ref={introRef} className={`mx-auto max-w-[800px] text-center transition-all duration-[600ms] ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-sans text-[0.75rem] font-bold uppercase tracking-[0.2em] text-secondary mb-6">
            Proven Results
          </p>
          <h2 className="font-serif text-[clamp(2.5rem,5vw,3.5rem)] font-bold leading-[1.2] tracking-tight text-foreground">
            Success Stories in <span className="text-secondary">Revenue Excellence</span>
          </h2>
          <div className="mt-8 h-px w-24 bg-secondary mx-auto" />
          <p className="font-sans mt-8 text-[clamp(1rem,1.5vw,1.125rem)] leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            Strategic interventions that transformed property performance through data-driven precision and hospitality expertise.
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          {tales.map((tale, i) => <div key={tale.id} data-index={i} className="flex">
              <Card className="h-full bg-card text-card-foreground transition-all duration-500 ease-out border border-border/20 rounded-lg overflow-hidden pt-0 flex flex-col group">
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img src={tale.image} data-wvc-srcset={tale.srcset} data-wvc-sizes={tale.sizes} alt={tale.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors duration-500" />
                </div>

                <div className="flex-1 px-8 py-8 flex flex-col">
                  <Badge variant="outline" className="border-secondary/30 text-secondary font-semibold tracking-widest uppercase text-[10px] rounded-md px-3 py-1 mb-6">
                    {tale.badge}
                  </Badge>

                  <h3 className="font-serif text-2xl font-semibold leading-tight text-foreground mb-4 group-hover:text-secondary transition-colors duration-300">
                    {tale.title}
                  </h3>

                  <p className="font-sans text-[0.9375rem] leading-relaxed text-muted-foreground mb-6 line-clamp-3">
                    {tale.teaser}
                  </p>

                  <div className="mt-auto pt-6 border-t border-border/20 flex flex-col gap-4">
                    <Button asChild variant="secondary" className="w-full rounded-md font-bold uppercase tracking-wider text-xs py-6">
                      <Link to={tale.link}>
                        View Case Study
                      </Link>
                    </Button>
                    
                    <Link to={tale.link} className="flex items-center justify-center gap-2 text-secondary group/link py-2">
                      <span className="font-sans text-[10px] font-bold uppercase tracking-[0.2em]">
                        Explore Details
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-out group-hover/link:translate-x-1" strokeWidth={2} />
                    </Link>
                  </div>
                </div>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}